import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Users,
  CheckCircle2,
  Gauge,
  TrendingUp,
  RefreshCw,
  Sofa,
  Plus,
  Trash2,
  Youtube,
  ExternalLink,
  Pencil,
  Save,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="stat-card">
    <div className="stat-icon">
      <Icon size={20} strokeWidth={1.6} />
    </div>
    <div>
      <p className="stat-value">{value}</p>
      <p className="stat-label">{label}</p>
    </div>
  </div>
);

const Bar = ({ label, count, pct, tag }) => (
  <div className="bar-row">
    <div className="bar-top">
      <span className="bar-label">
        {tag && <span className="bar-tag">{tag}</span>}
        {label}
      </span>
      <span className="bar-count">
        {count} <span className="bar-pct">({pct}%)</span>
      </span>
    </div>
    <div className="bar-track">
      <div className="bar-fill" style={{ width: `${pct}%` }} />
    </div>
  </div>
);

const ConsulenzeTable = ({ data }) => (
  <>
    <div className="stat-grid">
      <StatCard icon={Users} label="Richieste totali" value={data.total} />
    </div>
    {data.items.length === 0 ? (
      <div className="admin-empty">Ancora nessuna richiesta di consulenza.</div>
    ) : (
      <div className="orders-table-wrap" data-testid="consulenze-table">
        <table className="orders-table">
          <thead>
            <tr>
              {["Data", "Nome", "Cognome", "Email", "Telefono", "Giorno", "Fascia", "Dettagli"].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.map((c) => (
              <tr key={c.id}>
                <td>{(c.created_at || "").slice(0, 10)}</td>
                <td>{c.nome}</td>
                <td>{c.cognome}</td>
                <td><a href={`mailto:${c.email}`}>{c.email}</a></td>
                <td><a href={`tel:${c.telefono}`}>{c.telefono}</a></td>
                <td>{c.giorno}</td>
                <td>{c.fascia}</td>
                <td className="cons-details-cell">{c.dettagli || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("survey"); // survey | arredamento | orders | products
  const [survey, setSurvey] = useState(null);
  const [arr, setArr] = useState(null);
  const [orders, setOrders] = useState(null);
  const [products, setProducts] = useState([]);
  const [consulenze, setConsulenze] = useState(null);
  const EMPTY_FORM = { title: "", price: "", type: "ebook", youtube_url: "", content_youtube_url: "", description: "", preview_mode: "video" };
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [prodImage, setProdImage] = useState(null);
  const [currentImage, setCurrentImage] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [creating, setCreating] = useState(false);
  const [prodMsg, setProdMsg] = useState("");
  const [prodFile, setProdFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loadedTabs, setLoadedTabs] = useState({});

  const token = localStorage.getItem("fs_admin_token");
  const email = localStorage.getItem("fs_admin_email");

  const logout = () => {
    localStorage.removeItem("fs_admin_token");
    localStorage.removeItem("fs_admin_email");
    navigate("/area-riservata");
  };

  const headers = { Authorization: `Bearer ${token}` };
  const initialLoadRef = useRef(false);

  // Load ONLY the data needed for a given tab (lazy). Cached after first load.
  const loadTab = async (t, force = false) => {
    if (!force && loadedTabs[t]) return;
    setLoading(true);
    setError("");
    try {
      if (t === "survey") {
        const r = await axios.get(`${API}/admin/quiz-analytics`, { headers });
        setSurvey(r.data);
      } else if (t === "arredamento") {
        const r = await axios.get(`${API}/admin/arredamento-analytics`, { headers });
        setArr(r.data);
      } else if (t === "orders") {
        const r = await axios.get(`${API}/admin/orders`, { headers });
        setOrders(r.data);
      } else if (t === "consulenze") {
        const r = await axios.get(`${API}/admin/consulenze`, { headers });
        setConsulenze(r.data);
      } else if (t === "products") {
        const r = await axios.get(`${API}/admin/products`, { headers });
        setProducts(r.data);
      }
      setLoadedTabs((prev) => ({ ...prev, [t]: true }));
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        return;
      }
      setError("Impossibile caricare i dati. Riprova con Aggiorna.");
    } finally {
      setLoading(false);
    }
  };

  const changeTab = (t) => {
    setTab(t);
    loadTab(t);
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setProdFile(null);
    setProdImage(null);
    setCurrentImage("");
    setRemoveImage(false);
    setEditingId(null);
  };

  const startEdit = (p) => {
    setForm({
      title: p.title || "",
      price: p.price || "",
      type: p.type || "ebook",
      youtube_url: p.youtube_url || "",
      content_youtube_url: p.content_youtube_url || "",
      description: p.description || "",
      preview_mode: p.preview_mode || "video",
    });
    setProdFile(null);
    setProdImage(null);
    setCurrentImage(p.image ? (p.image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.image}` : p.image) : "");
    setRemoveImage(false);
    setEditingId(p.id);
    setProdMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const createProduct = async (e) => {
    e.preventDefault();
    setProdMsg("");
    if (!form.title.trim() || !String(form.price).trim()) {
      setProdMsg("Titolo e prezzo sono obbligatori.");
      return;
    }
    setCreating(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (prodFile) fd.append("file", prodFile);
      if (prodImage) fd.append("image", prodImage);
      if (editingId) {
        if (removeImage) fd.append("remove_image", "1");
        await axios.put(`${API}/admin/products/${editingId}`, fd, { headers });
      } else {
        await axios.post(`${API}/admin/products`, fd, { headers });
      }
      setProdMsg(editingId ? "Prodotto aggiornato con successo." : "Prodotto creato con successo.");
      resetForm();
      await loadTab("products", true);
    } catch (err) {
      setProdMsg(err?.response?.data?.detail || "Errore nel salvataggio del prodotto.");
    } finally {
      setCreating(false);
    }
  };

  const deleteProduct = async (pid) => {
    try {
      await axios.delete(`${API}/admin/products/${pid}`, { headers });
      await loadTab("products", true);
    } catch (err) {
      setProdMsg(err?.response?.data?.detail || "Impossibile eliminare il prodotto.");
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/area-riservata");
      return;
    }
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      loadTab("survey");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div>
          <p className="logo-mark" style={{ fontSize: 17 }}>
            <span className="l1">Francesca</span>
            <span className="l2">Spinazzola</span>
          </p>
        </div>
        <div className="admin-header-right">
          <span className="admin-user">{email}</span>
          <button className="admin-icon-btn" onClick={() => loadTab(tab, true)} title="Aggiorna">
            <RefreshCw size={16} strokeWidth={1.7} />
          </button>
          <button className="admin-logout" onClick={logout}>
            <LogOut size={16} strokeWidth={1.7} />
            Esci
          </button>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-title-row">
          <p className="section-eyebrow">Back office</p>
          <h1 className="admin-page-title serif">Analisi dei test</h1>
        </div>

        <div className="admin-tabs">
          <button
            className={`admin-tab ${tab === "survey" ? "active" : ""}`}
            onClick={() => changeTab("survey")}
          >
            Potenziale struttura
          </button>
          <button
            className={`admin-tab ${tab === "arredamento" ? "active" : ""}`}
            onClick={() => changeTab("arredamento")}
          >
            Stile arredamento
          </button>
          <button
            className={`admin-tab ${tab === "orders" ? "active" : ""}`}
            onClick={() => changeTab("orders")}
          >
            Ordini
          </button>
          <button
            className={`admin-tab ${tab === "products" ? "active" : ""}`}
            onClick={() => changeTab("products")}
          >
            Prodotti
          </button>
          <button
            className={`admin-tab ${tab === "consulenze" ? "active" : ""}`}
            onClick={() => changeTab("consulenze")}
            data-testid="admin-tab-consulenze"
          >
            Consulenze
          </button>
        </div>

        {loading && <p className="admin-muted">Caricamento dati...</p>}
        {error && <p className="admin-error">{error}</p>}

        {/* SURVEY (home staging) */}
        {!loading && tab === "survey" && survey && (
          <>
            <div className="stat-grid">
              <StatCard icon={Users} label="Risposte totali" value={survey.total} />
              <StatCard icon={CheckCircle2} label="Test completati" value={survey.completed} />
              <StatCard icon={Gauge} label="Punteggio medio" value={`${survey.avg_score} / ${survey.max_score}`} />
              <StatCard icon={TrendingUp} label="Preparazione media" value={`${survey.avg_perc}%`} />
            </div>

            {survey.total === 0 ? (
              <div className="admin-empty">
                Ancora nessuna risposta al test del potenziale struttura.
              </div>
            ) : (
              <>
                <div className="panel-grid">
                  <section className="panel">
                    <h2 className="panel-title">Livello dei rispondenti</h2>
                    <p className="panel-sub">Dove vengono indirizzati in base alle risposte</p>
                    <div className="bars">
                      {survey.levels.map((l) => (
                        <Bar key={l.rule} label={l.label} count={l.count} pct={l.pct} />
                      ))}
                    </div>
                  </section>
                  <section className="panel">
                    <h2 className="panel-title">Urgenza (Domanda 6)</h2>
                    <p className="panel-sub">Quanto sono pronti a intervenire</p>
                    <div className="bars">
                      {survey.urgency.map((u) => (
                        <Bar key={u.urgenza} label={u.label} count={u.count} pct={u.pct} />
                      ))}
                    </div>
                  </section>
                </div>

                <h2 className="admin-section-heading serif">Dettaglio per domanda</h2>
                <div className="question-list">
                  {survey.questions.map((q) => (
                    <section className="panel question-panel" key={q.id}>
                      <div className="q-head">
                        <span className="q-num">Domanda {q.id}</span>
                        <span className="q-answered">{q.answered} risposte</span>
                      </div>
                      <h3 className="q-text">{q.question}</h3>
                      <div className="bars">
                        {q.options.map((o) => (
                          <Bar key={o.key} label={o.text} count={o.count} pct={o.pct} tag={o.key.toUpperCase()} />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ARREDAMENTO */}
        {!loading && tab === "arredamento" && arr && (
          <>
            <div className="stat-grid">
              <StatCard icon={Users} label="Risposte totali" value={arr.total} />
              <StatCard icon={CheckCircle2} label="Test completati" value={arr.completed} />
              <StatCard
                icon={Sofa}
                label="Stile più frequente"
                value={
                  arr.total
                    ? (arr.styles.slice().sort((a, b) => b.count - a.count)[0]?.label || "—")
                    : "—"
                }
              />
            </div>

            {arr.total === 0 ? (
              <div className="admin-empty">
                Ancora nessuna risposta al quiz sullo stile di arredamento.
              </div>
            ) : (
              <>
                <section className="panel" style={{ marginBottom: 30 }}>
                  <h2 className="panel-title">Distribuzione degli stili</h2>
                  <p className="panel-sub">Quale profilo esce più spesso</p>
                  <div className="bars">
                    {arr.styles.map((s) => (
                      <Bar key={s.key} label={s.label} count={s.count} pct={s.pct} tag={s.key} />
                    ))}
                  </div>
                </section>

                <h2 className="admin-section-heading serif">Dettaglio per domanda</h2>
                <div className="question-list">
                  {arr.questions.map((q) => (
                    <section className="panel question-panel" key={q.id}>
                      <div className="q-head">
                        <span className="q-num">Domanda {q.id}</span>
                        <span className="q-answered">{q.answered} risposte</span>
                      </div>
                      <h3 className="q-text">{q.question}</h3>
                      <div className="bars">
                        {q.options.map((o) => (
                          <Bar key={o.key} label={o.label} count={o.count} pct={o.pct} tag={o.key} />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </>
            )}
          </>
        )}
        {/* ORDERS */}
        {!loading && tab === "orders" && orders && (
          <>
            <div className="stat-grid">
              <StatCard icon={Users} label="Ordini totali" value={orders.total} />
              <StatCard icon={CheckCircle2} label="Ordini pagati" value={orders.paid} />
            </div>

            {orders.orders.length === 0 ? (
              <div className="admin-empty">Ancora nessun ordine.</div>
            ) : (
              <div className="orders-table-wrap">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Stato</th>
                      <th>Prodotto</th>
                      <th>Importo</th>
                      <th>Nome</th>
                      <th>Cognome</th>
                      <th>Email</th>
                      <th>Cod. Fiscale</th>
                      <th>P. IVA</th>
                      <th>Indirizzo</th>
                      <th>Città</th>
                      <th>CAP</th>
                      <th>PayPal ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.orders.map((o) => (
                      <tr key={o.id}>
                        <td>
                          {(o.paid_at || o.created_at || "").slice(0, 10)}
                        </td>
                        <td>
                          <span className={`order-status ${o.status}`}>
                            {o.status === "paid" ? "Pagato" : "Creato"}
                          </span>
                        </td>
                        <td>{o.product_title}</td>
                        <td>
                          € {Number(o.amount).toFixed(2).replace(".", ",")}
                        </td>
                        <td>{o.nome}</td>
                        <td>{o.cognome}</td>
                        <td>{o.email}</td>
                        <td>{o.codice_fiscale || "—"}</td>
                        <td>{o.partita_iva || "—"}</td>
                        <td>{o.via}</td>
                        <td>{o.citta}</td>
                        <td>{o.cap}</td>
                        <td>{o.paypal_order_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        {!loading && tab === "consulenze" && consulenze && (
          <ConsulenzeTable data={consulenze} />
        )}
        {/* PRODUCTS management */}
        {!loading && tab === "products" && (
          <>
            <section className="panel" style={{ marginBottom: 26 }}>
              <h2 className="panel-title" data-testid="prod-form-title">
                {editingId ? `Modifica prodotto: ${form.title}` : "Crea un nuovo prodotto"}
              </h2>
              <p className="panel-sub">
                Il prezzo indicato sarà quello addebitato da PayPal. Il link YouTube
                verrà mostrato in cima alla pagina del prodotto.
              </p>
              <form onSubmit={createProduct} className="prod-form">
                <div className="billing-field">
                  <label>Titolo <span className="req">*</span></label>
                  <input
                    data-testid="prod-title-input"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </div>
                <div className="billing-field">
                  <label>Tipo prodotto</label>
                  <select
                    className="prod-select"
                    data-testid="prod-type-select"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="ebook">Ebook</option>
                    <option value="corso">Video Corso</option>
                    <option value="call">Call</option>
                  </select>
                </div>
                <div className="billing-field">
                  <label>Prezzo (EUR) <span className="req">*</span></label>
                  <input
                    data-testid="prod-price-input"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="es. 24.90"
                  />
                </div>
                <div className="billing-field">
                  <label>Link YouTube anteprima</label>
                  <input
                    value={form.youtube_url}
                    onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                    placeholder="https://youtu.be/... (mostrato in pagina)"
                  />
                </div>
                <div className="billing-field">
                  <label>Immagine anteprima (ottimizzata automaticamente)</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="prod-file"
                    data-testid="prod-image-input"
                    onChange={(e) => setProdImage(e.target.files?.[0] || null)}
                  />
                  {(prodImage || (currentImage && !removeImage)) && (
                    <div className="prod-img-preview">
                      <img
                        src={prodImage ? URL.createObjectURL(prodImage) : currentImage}
                        alt="Anteprima"
                        data-testid="prod-image-preview"
                      />
                      {!prodImage && editingId && (
                        <button
                          type="button"
                          className="prod-img-remove"
                          data-testid="prod-image-remove-btn"
                          onClick={() => setRemoveImage(true)}
                        >
                          Rimuovi immagine
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="billing-field">
                  <label>Anteprima da mostrare in pagina</label>
                  <div className="prod-toggle" data-testid="prod-preview-toggle">
                    {[["video", "Video YouTube"], ["image", "Immagine"]].map(([v, l]) => (
                      <button
                        type="button"
                        key={v}
                        data-testid={`prod-preview-${v}-btn`}
                        className={form.preview_mode === v ? "active" : ""}
                        onClick={() => setForm({ ...form, preview_mode: v })}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="billing-field">
                  <label>Link YouTube del corso (consegnato dopo l'acquisto)</label>
                  <input
                    value={form.content_youtube_url}
                    onChange={(e) => setForm({ ...form, content_youtube_url: e.target.value })}
                    placeholder="https://youtu.be/... (video riservato)"
                  />
                </div>
                <div className="billing-field full">
                  <label>Descrizione</label>
                  <textarea
                    className="prod-textarea"
                    rows={4}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
                <div className="billing-field full">
                  <label>File da scaricare dopo il pagamento (PDF, ecc.)</label>
                  <input
                    type="file"
                    className="prod-file"
                    onChange={(e) => setProdFile(e.target.files?.[0] || null)}
                  />
                  {prodFile && (
                    <span className="prod-file-name">{prodFile.name}</span>
                  )}
                </div>
                <div className="full">
                  {prodMsg && <p className="prod-msg">{prodMsg}</p>}
                  <div className="prod-form-actions">
                    <button type="submit" className="admin-submit" disabled={creating} data-testid="prod-submit-btn">
                      {editingId ? <Save size={16} strokeWidth={1.8} /> : <Plus size={16} strokeWidth={1.8} />}
                      <span>
                        {creating ? "Salvataggio..." : editingId ? "Salva modifiche" : "Crea prodotto"}
                      </span>
                    </button>
                    {editingId && (
                      <button type="button" className="prod-cancel" onClick={resetForm} data-testid="prod-cancel-edit-btn">
                        Annulla
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </section>

            <h2 className="admin-section-heading serif">Prodotti pubblicati</h2>
            <div className="prod-list">
              {products.map((p) => (
                <div className="prod-card" key={p.id}>
                  <div className="prod-card-main">
                    <span className="prod-type">{p.subtitle || p.type}</span>
                    <span className="prod-title">{p.title}</span>
                    <span className="prod-price">
                      € {Number(p.price).toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                  <div className="prod-card-actions">
                    {p.youtube_url && (
                      <a
                        className="admin-icon-btn"
                        href={p.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Video"
                      >
                        <Youtube size={16} strokeWidth={1.7} />
                      </a>
                    )}
                    <a
                      className="admin-icon-btn"
                      href={`/commerce/${p.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Apri pagina"
                    >
                      <ExternalLink size={16} strokeWidth={1.7} />
                    </a>
                    {p.editable && (
                      <button
                        className="admin-icon-btn"
                        onClick={() => startEdit(p)}
                        title="Modifica"
                        data-testid={`prod-edit-btn-${p.id}`}
                      >
                        <Pencil size={16} strokeWidth={1.7} />
                      </button>
                    )}
                    {p.deletable && (
                      <button
                        data-testid={`prod-delete-btn-${p.id}`}
                        className="admin-icon-btn danger"
                        onClick={() => deleteProduct(p.id)}
                        title="Elimina"
                      >
                        <Trash2 size={16} strokeWidth={1.7} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
